"use client";

import { JSX, useEffect, useRef } from "react";

import { usePathname } from "next/navigation";

import { getPageViewProps } from "@/components/vendor/Analytics/pageViewPropsSnippet";
import { SEGMENT_ANONYMOUS_ID_COOKIE } from "@/constants/cookies";
import { readCookie, setCookie } from "@/utils/cookies";
import { registerGa4ColdPageViewHook } from "@/utils/sendGA4PageView";

declare global {
  interface Window {
    analytics?: {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      page: (title: string, options?: Record<string, any>) => void | Promise<any>;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      track: (event: string, props?: Record<string, any>) => void | Promise<any>;
      identify: (
        userIdOrTraits: string | Record<string, unknown>,
        props?: Record<string, unknown>
      ) => void;
      ready?: (callback: () => void) => void;
      user?: () => { anonymousId: () => string };
      setAnonymousId?: (id: string) => void;
    };
  }
}

function setSegmentAnonymousIdCookieOnce(): void {
  if (readCookie(SEGMENT_ANONYMOUS_ID_COOKIE)) return;
  const a = window.analytics;
  if (!a?.ready) return;
  a.ready(() => {
    if (readCookie(SEGMENT_ANONYMOUS_ID_COOKIE)) return;
    try {
      const an = typeof window !== "undefined" ? window.analytics : undefined;
      const id = an?.user?.().anonymousId?.();
      if (id) setCookie(SEGMENT_ANONYMOUS_ID_COOKIE, id, 30);
    } catch {
      // ignore
    }
  });
}

export const getSegmentAnonymousId = () => readCookie(SEGMENT_ANONYMOUS_ID_COOKIE);

export function AnalyticsTracker(): JSX.Element {
  const pathname = usePathname();
  const prevPathRef = useRef<string | null>(null);

  /*
   * Cold-load page_view fires in gtag config snippet (after Segment pre-seed).
   * Hook allows OptanonWrapper to send if head snippet did not.
   */
  useEffect(() => {
    registerGa4ColdPageViewHook();
    setSegmentAnonymousIdCookieOnce();
  }, []);

  /*
   * Segment SPA page calls only — not GA4.
   */
  useEffect(() => {
    // First load: document.referrer. In-app navigation: previous page URL.
    const page_referrer = prevPathRef.current
      ? window.location.origin + prevPathRef.current
      : document.referrer;
    prevPathRef.current = pathname;

    // Delay so document.title is the new page's title.
    const timer = setTimeout(() => {
      if (typeof window !== "undefined" && window.analytics) {
        const name = document.title?.split(" | ")[0];
        window.analytics.page(name, {
          ...getPageViewProps(pathname, page_referrer),
          segment_anonymous_id: readCookie(SEGMENT_ANONYMOUS_ID_COOKIE) || null,
          path: pathname,
          url: window.location.href,
          userAgent: navigator.userAgent,
          title: name,
        });
      } else {
        // eslint-disable-next-line no-console
        console.error("window.analytics not loaded");
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [pathname]);

  return null;
}
